import * as React from "react";
import Button from "@material-ui/core/Button";
import { SagaService } from "front-end-lib";
import { ISelectData, BktSelect } from "front-end-lib/core";
import {
  MddDataContainer,
  MddValidatedTextField,
  MddCheckbox,
  withMddStudy
} from "../../../components";
import { MAX_TEXT_LENGTH } from "../../../constants";
import { Validations } from "../../../model/classes/validations";
import {
  IMddStudyProps,
  ILearningPlan,
  IScoringCourse,
  IAssignmentRuleDetails
} from "../../../model";
import { eAssignmentRuleSaga, eStudySaga } from "../../../sagas";
import { runSagaAsync, isLoading, history } from "../../../utils";

const RULE_TYPE_DISPLAY = "Remediation";
const NEW_RULE_ID = "new";
const GUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type IMddAddAssignmentRuleProps = IMddStudyProps<{ ruleId: string }>;

const toSelectData = (
  items: Array<{ id: string; name: string }>
): ISelectData[] =>
  items.map(item => ({
    value: item.id.toString(),
    label: item.name,
    key: item.id.toString()
  }));

const toCourseSelectData = (courses: IScoringCourse[]): ISelectData[] =>
  courses.map(course => ({
    value: course.idCourse.toString(),
    label: course.name,
    key: course.idCourse.toString()
  }));

const _MddAddAssignmentRule = (props: IMddAddAssignmentRuleProps) => {
  const {
    study,
    match: {
      params: { id: routeStudyId, ruleId: routeRuleId }
    }
  } = props;

  const isEditMode = routeRuleId !== NEW_RULE_ID;
  const isValidRuleId = isEditMode ? GUID_REGEX.test(routeRuleId) : true;
  const title = isEditMode ? "Edit Assignment Rule" : "Add Assignment Rule";

  React.useEffect(() => {
    if (!isValidRuleId) {
      history.push(`/study/${routeStudyId}/assignmentrules`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValidRuleId]);

  const [learningPlans, setLearningPlans] = React.useState<ILearningPlan[]>([]);
  const [ruleName, setRuleName] = React.useState("");
  const [ruleNameHasError, setRuleNameHasError] = React.useState(false);
  const [triggerLearningPlanId, setTriggerLearningPlanId] = React.useState("");
  const [
    triggerLearningPlanTouched,
    setTriggerLearningPlanTouched
  ] = React.useState(false);
  const [triggerCourseId, setTriggerCourseId] = React.useState("");
  const [triggerCourseTouched, setTriggerCourseTouched] = React.useState(false);
  const [assignLearningPlanId, setAssignLearningPlanId] = React.useState("");
  const [
    assignLearningPlanTouched,
    setAssignLearningPlanTouched
  ] = React.useState(false);
  const [isActive, setIsActive] = React.useState(true);
  const [existingRule, setExistingRule] = React.useState<
    IAssignmentRuleDetails
  >();

  React.useEffect(() => {
    if (!isValidRuleId) {
      return;
    }
    (async () => {
      if (isEditMode) {
        const rule = await runSagaAsync<IAssignmentRuleDetails>({
          startType: eAssignmentRuleSaga.FETCH_ASSIGNMENT_RULE.toString(),
          successType: eAssignmentRuleSaga.FETCH_ASSIGNMENT_RULE_SUCCESS.toString(),
          payload: { studyId: routeStudyId, ruleId: routeRuleId }
        });
        setLearningPlans([
          {
            id: rule.triggerLearningPlanId.toString(),
            name: rule.triggerLearningPlanName,
            courses: [
              {
                idCourse: rule.triggerCourseId.toString(),
                name: rule.triggerCourseName
              }
            ]
          },
          {
            id: rule.assignLearningPlanId.toString(),
            name: rule.assignLearningPlanName
          }
        ]);
        setExistingRule(rule);
        setRuleName(rule.ruleName);
        setTriggerLearningPlanId(rule.triggerLearningPlanId.toString());
        setTriggerCourseId(rule.triggerCourseId.toString());
        setAssignLearningPlanId(rule.assignLearningPlanId.toString());
        setIsActive(rule.isActive);
      } else {
        const plans = await runSagaAsync<ILearningPlan[]>({
          startType: eStudySaga.FETCH_LEARNING_PLANS.toString(),
          successType: eStudySaga.FETCH_LEARNING_PLANS_SUCCESS.toString(),
          payload: { studyId: routeStudyId, includeTrainingCourses: true }
        });
        setLearningPlans(plans || []);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleTriggerLearningPlanChange = (learningPlanId: string) => {
    setTriggerLearningPlanId(learningPlanId);
    setTriggerCourseId("");
  };

  const learningPlanSelectData = React.useMemo(
    () => toSelectData(learningPlans || []),
    [learningPlans]
  );

  const scoringCourseSelectData = React.useMemo(() => {
    const selectedLearningPlan = (learningPlans || []).find(
      lp => lp.id.toString() === triggerLearningPlanId
    );
    return toCourseSelectData(selectedLearningPlan?.courses || []);
  }, [learningPlans, triggerLearningPlanId]);

  const loadingSave = isLoading(
    `undefined_${eAssignmentRuleSaga.SAVE_ASSIGNMENT_RULE}`
  );

  const disableSaveButton = isEditMode
    ? loadingSave || isActive === existingRule?.isActive
    : loadingSave ||
      !ruleName ||
      ruleNameHasError ||
      !triggerLearningPlanId ||
      !triggerCourseId ||
      !assignLearningPlanId;

  const handleSubmit = () => {
    const payload = isEditMode
      ? { studyId: routeStudyId, ruleId: routeRuleId, isActive }
      : {
          studyId: routeStudyId,
          ruleName,
          triggerLearningPlanId: parseInt(triggerLearningPlanId, 10),
          triggerCourseId: parseInt(triggerCourseId, 10),
          assignedLearningPlanId: parseInt(assignLearningPlanId, 10)
        };

    SagaService.dispatchSaga({
      type: eAssignmentRuleSaga.SAVE_ASSIGNMENT_RULE,
      payload
    });
  };

  const handleCancel = () => {
    history.push(`/study/${routeStudyId}/assignmentrules`);
  };

  return (
    <MddDataContainer
      id="AddAssignmentRule"
      breadcrumbs={[
        { display: "All Studies", route: "/study" },
        {
          display: study.StudyName,
          route: `/study/${routeStudyId}/assignmentrules`
        },
        { display: title }
      ]}
      title={title}
    >
      <div className="mdd-assignment-rule--card">
        <div className="mdd-assignment-rule--section-header">Rule Details</div>
        <div className="mdd-assignment-rule--card-content">
          <div className="mdd-assignment-rule--field">
            <div className="mdd-assignment-rule--field-label">Rule Type</div>
            <BktSelect
              menuProps={{}}
              textProps={{
                id: "ruleType",
                label: "",
                value: "REMEDIATION",
                disabled: true,
                placeholder: RULE_TYPE_DISPLAY,
                onChange: () => {}
              }}
              selectData={[
                {
                  value: "REMEDIATION",
                  label: RULE_TYPE_DISPLAY,
                  key: "REMEDIATION"
                }
              ]}
              selectMenuProps={{}}
            />
          </div>

          <MddValidatedTextField
            id="ruleName"
            labelProps={{ labelText: "Rule Name" }}
            textFieldProps={{
              textProps: {
                inputProps: {
                  id: "ruleNameTextField",
                  maxLength: MAX_TEXT_LENGTH.ASSIGNMENT_RULE_NAME
                },
                variant: "outlined",
                required: true,
                type: "text",
                disabled: isEditMode,
                placeholder: "Enter rule name",
                onChange: e => setRuleName(e.target.value),
                value: ruleName
              }
            }}
            validationForOnBlur={[Validations.required]}
            validationerror={setRuleNameHasError}
          />
        </div>
      </div>

      <div className="mdd-assignment-rule--card">
        <div className="mdd-assignment-rule--section-header">Trigger</div>
        <div className="mdd-assignment-rule--card-content">
          <div className="mdd-assignment-rule--field">
            <div className="mdd-assignment-rule--field-label">
              Learning Plan
            </div>
            <BktSelect
              defaultSelect
              menuProps={{}}
              textProps={{
                id: "triggerLearningPlan",
                label: "",
                value: triggerLearningPlanId,
                disabled: isEditMode,
                placeholder: "Select a Learning Plan",
                onChange: event =>
                  handleTriggerLearningPlanChange(event.target.value),
                onBlur: () => setTriggerLearningPlanTouched(true),
                error: triggerLearningPlanTouched && !triggerLearningPlanId,
                helperText:
                  triggerLearningPlanTouched && !triggerLearningPlanId
                    ? Validations.required.validationMessage
                    : ""
              }}
              selectData={learningPlanSelectData}
              selectMenuProps={{
                displayEmpty: true,
                renderValue: (value: unknown) =>
                  value
                    ? learningPlanSelectData.find(item => item.value === value)
                        ?.label
                    : "Select a Learning Plan"
              }}
            />
          </div>

          <div className="mdd-assignment-rule--field">
            <div className="mdd-assignment-rule--field-label">
              Scoring Course Evaluated
            </div>
            <BktSelect
              defaultSelect
              menuProps={{}}
              textProps={{
                id: "triggerCourse",
                label: "",
                value: triggerCourseId,
                disabled: isEditMode || !triggerLearningPlanId,
                placeholder: "Select a Scoring Course",
                onChange: event => setTriggerCourseId(event.target.value),
                onBlur: () => setTriggerCourseTouched(true),
                error: triggerCourseTouched && !triggerCourseId,
                helperText:
                  triggerCourseTouched && !triggerCourseId
                    ? Validations.required.validationMessage
                    : ""
              }}
              selectData={scoringCourseSelectData}
              selectMenuProps={{
                displayEmpty: true,
                renderValue: (value: unknown) =>
                  value
                    ? scoringCourseSelectData.find(item => item.value === value)
                        ?.label
                    : "Select a Scoring Course"
              }}
            />
          </div>

          <div className="mdd-assignment-rule--instructional-text">
            <span>IF</span> Course = Fail <strong>AND</strong> Score &ge;
            Remediation Threshold
            <br />
            <span>THEN</span> Assign Remediation Learning Plan
          </div>
        </div>
      </div>

      <div className="mdd-assignment-rule--card">
        <div className="mdd-assignment-rule--section-header">Assignment</div>
        <div className="mdd-assignment-rule--card-content">
          <div className="mdd-assignment-rule--field">
            <div className="mdd-assignment-rule--field-label">
              Remediation Learning Plan
            </div>
            <BktSelect
              defaultSelect
              menuProps={{}}
              textProps={{
                id: "assignLearningPlan",
                label: "",
                value: assignLearningPlanId,
                disabled: isEditMode,
                placeholder: "Select a Learning Plan",
                onChange: event => setAssignLearningPlanId(event.target.value),
                onBlur: () => setAssignLearningPlanTouched(true),
                error: assignLearningPlanTouched && !assignLearningPlanId,
                helperText:
                  assignLearningPlanTouched && !assignLearningPlanId
                    ? Validations.required.validationMessage
                    : ""
              }}
              selectData={learningPlanSelectData}
              selectMenuProps={{
                displayEmpty: true,
                renderValue: (value: unknown) =>
                  value
                    ? learningPlanSelectData.find(item => item.value === value)
                        ?.label
                    : "Select a Learning Plan"
              }}
            />
          </div>

          <div className="mdd-assignment-rule--instructional-text">
            <div>
              <strong>Evaluates when:</strong> Scores are calculated · On score
              submission or Master/Country Acceptable Score update.
            </div>
            <div>
              <strong>Does not apply if:</strong> Rater is inactive · Already
              assigned to remediation LP · Does not meet experience eligibility
              · Study Qualification = Qualified, Not Qualified, or Not Rating ·
              Scores were previously calculated (rule is not retroactive).
            </div>
          </div>
        </div>
      </div>

      {isEditMode && (
        <MddCheckbox
          lblText="Active"
          chkBoxProps={{
            id: "chkAssignmentRuleIsActive",
            color: "primary",
            checked: isActive,
            onChange: () => setIsActive(!isActive)
          }}
        />
      )}

      <div className="mdd-assignment-rule--save-button-section">
        <Button
          id="btnCancelAssignmentRule"
          className="mdd-assignment-rule--cancel-button"
          color="primary"
          disableFocusRipple
          disableRipple
          variant="outlined"
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          id="btnSaveAssignmentRule"
          className="mdd-assignment-rule--save-button"
          color="primary"
          disableFocusRipple
          disableRipple
          variant="contained"
          disabled={disableSaveButton}
          onClick={handleSubmit}
        >
          Save
        </Button>
      </div>
    </MddDataContainer>
  );
};

export const MddAddAssignmentRule = withMddStudy()(_MddAddAssignmentRule);
